import { z } from "zod";
import { offerDocumentSchema } from "./documentsSchemas";
import { funderSchema } from "@/api/http/schemas/funderSchemas";

const dateSchema = z.string().regex(/^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/);

export enum ProductId {
  Arloc = 1,
  LineOfCredit = 2,
  ReceivablesPurchase = 3,
  TermLoan = 4,
  EquipmentFinancing = 5,
  Erc = 9,
  Sba = 10,
}

export const offerProductIdSchema = z.nativeEnum(ProductId);

export enum PaymentPeriod {
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  BiWeekly = 4,
}

const paymentPeriodSchema = z.nativeEnum(PaymentPeriod);

export enum OfferStatus {
  Accepted = 1,
  Sent = 2,
  Declined = 3,
  Retracted = 4,
}

const offerStatusSchema = z.nativeEnum(OfferStatus);

export const offerDataSchema = z.object({
  base_rate: z.number().optional().nullable(),
  erc_credit_amount: z.number().optional().nullable(),
  erc_filing_date: dateSchema.optional().nullable(),
  funding_amount: z.number().optional().nullable(),
  initial_draw_fee: z.number().optional().nullable(),
  interest_charged: z.number().optional().nullable(),
  max_facility_amount: z.number().optional().nullable(),
  max_line_of_credit: z.number().optional().nullable(),
  max_sell_factor_rate: z.number().optional().nullable(),
  max_sell_interest_rate: z.number().optional().nullable(),
  max_term: z.number().optional().nullable(),
  monthly_interest: z.number().optional().nullable(),
  number_of_payments: z.number().nullish(),
  offer_amount: z.number().optional().nullable(),
  offer_expiration_date: dateSchema.optional().nullable(),
  offer_generated_date: dateSchema.optional().nullable(),
  payback_amount: z.number().optional().nullable(),
  payment_amount: z.number().optional().nullable(),
  payment_frequency: paymentPeriodSchema.nullish(),
  term: z.number().optional().nullable(),
  term_length: z.number().optional().nullable(),
  term_length_timeframe: z.number().optional().nullable(),
  total_financed_amount: z.number().optional().nullable(),
});

export type OfferData = z.infer<typeof offerDataSchema>;

export const offerDetailsTypeSchema = z.union([
  z.literal("currency"),
  z.literal("date"),
  z.literal("float"),
  z.literal("integer"),
  z.literal("options"),
  z.literal("percentage"),
  z.literal("string"),
  z.literal("text"),
  z.literal("url"),
]);

export type OfferDetailsType = z.infer<typeof offerDetailsTypeSchema>;

export const offerDetailsSectionSchema = z.union([z.literal("primary"), z.literal("expanded")]);

export type OfferDetailsSection = z.infer<typeof offerDetailsSectionSchema>;

export const offerDetailsSchema = z.object({
  label: z.string(),
  name: z.string(),
  section: offerDetailsSectionSchema,
  options: z.union([z.array(z.string()), z.record(z.string(), z.string())]).optional(),
  type: offerDetailsTypeSchema,
  value: z.union([z.string(), z.number()]).optional().nullable(),
  weight: z.number(),
  dynamic: z.boolean(),
});

export const OfferTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  is_fallback: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export type OfferDetails = z.infer<typeof offerDetailsSchema>;

export const offerSchema = z.object({
  closing_details: z.array(offerDetailsSchema),
  created_at: z.string().datetime(),
  declined_reason: z.string().optional().nullable(),
  funder: funderSchema.optional(),
  has_closing_details: z.boolean(),
  has_checkout_snapshot: z.boolean().optional(),
  id: z.string(),
  offer_details: z.array(offerDetailsSchema),
  product_id: offerProductIdSchema,
  required_stips: z.array(offerDocumentSchema),
  status: offerStatusSchema.nullable(),
  funder_id: z.string(),
  offer_template_id: z.string(),
  template: OfferTemplateSchema,
});

export type Offer = z.infer<typeof offerSchema>;

export const groupedOfferSchema = z.object({
  id: z.string(),
  funder_id: z.string(),
  offer_template_id: z.string(),
  weight: z.number(),
  offers: z.array(offerSchema),
});

export type GroupedOffer = z.infer<typeof groupedOfferSchema>;

export const updatedOfferSchema = offerSchema.omit({ required_stips: true });

export type UpdatedOffer = z.infer<typeof updatedOfferSchema>;

export const offerDynamicDetailsSchema = z.object({
  name: offerDetailsSchema.shape.name,
  values: z.array(offerDetailsSchema.shape.value),
  type: offerDetailsTypeSchema,
  items: z.array(offerDetailsSchema.extend({ offer_id: offerSchema.shape.id })),
  options: offerDetailsSchema.shape.options,
  label: offerDetailsSchema.shape.label,
  weight: offerDetailsSchema.shape.weight,
  offers: z.record(offerSchema.shape.id, offerDetailsSchema.shape.value),
});

export type OfferDynamicDetails = z.infer<typeof offerDynamicDetailsSchema>;
